// ─────────────────────────────────────────────────────────────────────────────
// 🔑 BACKGROUND SCROLL LOCK FOR A MODAL (fp-smartest, Jesse 2026-11-01), iOS Safari included. `overflow: hidden` on body
// alone does not stop iOS Safari scrolling the page behind a fixed overlay, so the body is pinned instead: position
// fixed at top = −scrollY (the page does not jump), width 100%, and on release every style is put back and the window
// scrolled to where it was. Nested locks count; only the last release restores. The modal's own box scrolls itself
// (overflow auto + overscroll-behavior: contain), so a wheel or a touch drag over it never reaches the page.
// 🚨 NO TOUCH LISTENERS, EVER (fix/signing-scroll): the lock is styles only. It never calls preventDefault on
// touchstart / touchmove, so a touch scroll inside the modal's scroller works from the FIRST gesture
// (backend/test/signingScroll.test.js pins this; e2e 320 swipes with real touch events).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';

let locks = 0;
let saved = null;

export function lockBodyScroll() {
  if (typeof document === 'undefined') return () => {};
  locks += 1;
  if (locks === 1) {
    const body = document.body; const html = document.documentElement;
    const y = window.scrollY || window.pageYOffset || 0;
    saved = { y, body: [body.style.position, body.style.top, body.style.left, body.style.right, body.style.width, body.style.overflow],
      html: html.style.overflow };
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    locks = Math.max(0, locks - 1);
    if (locks === 0 && saved) {
      const body = document.body; const html = document.documentElement;
      [body.style.position, body.style.top, body.style.left, body.style.right, body.style.width, body.style.overflow] = saved.body;
      html.style.overflow = saved.html;
      const y = saved.y;
      saved = null;
      window.scrollTo(0, y);
    }
  };
}

/** Lock the page behind while `active`. */
export default function useBodyScrollLock(active = true) {
  useEffect(() => (active ? lockBodyScroll() : undefined), [active]);
}
